import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { ArrowLeft, Lock, Eye, EyeOff, Loader2, CheckCircle2, KeyRound } from "lucide-react";

export default function Reset() {
  const loc = useLocation();
  const nav = useNavigate();
  const [token, setToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const t = params.get("token") || "";
    if (!t) { setErr("Missing reset token. Open the link from your reset email."); }
    setToken(t);
  }, [loc.search]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw, confirm_password: pw2 });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white px-6 py-10 flex items-center" data-testid="reset-page">
      <div className="max-w-sm w-full mx-auto">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-white/60 mb-6"><ArrowLeft className="w-3.5 h-3.5"/>Back to login</Link>

        <div className="w-14 h-14 rounded-2xl bg-[#FF7A00]/15 border border-[#FF7A00]/30 flex items-center justify-center mb-4">
          <KeyRound className="w-6 h-6 text-[#FF7A00]"/>
        </div>

        {!done ? (
          <>
            <h1 className="font-outfit font-extrabold text-3xl mb-1">Set a new password</h1>
            <p className="text-sm text-white/60 mb-6">Choose a strong password you&apos;ll remember.</p>

            <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
              <div className="relative">
                <Lock className="w-4 h-4 text-white/40 absolute left-3.5 top-4"/>
                <input
                  data-testid="reset-new-password"
                  type={show ? "text" : "password"}
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  placeholder="New password"
                  autoComplete="new-password"
                  required
                  className="w-full pl-10 pr-10 py-3.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00] text-white placeholder-white/40"
                />
                <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-3.5 text-white/50">
                  {show ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 text-white/40 absolute left-3.5 top-4"/>
                <input
                  data-testid="reset-confirm-password"
                  type={show ? "text" : "password"}
                  value={pw2}
                  onChange={e => setPw2(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  required
                  className="w-full pl-10 pr-3 py-3.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00] text-white placeholder-white/40"
                />
              </div>
              {err && <div className="text-[#FF2A5F] text-sm" data-testid="reset-err">{err}</div>}
              <button disabled={busy || !token} type="submit" data-testid="reset-submit"
                className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF7A00] text-black font-outfit font-extrabold disabled:opacity-50 active:scale-95 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin"/>Saving…</> : "Save new password"}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="reset-done" className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-7 h-7 text-emerald-400"/>
            </div>
            <h2 className="font-outfit font-extrabold text-2xl mb-1">Password updated</h2>
            <p className="text-sm text-white/65 mb-5">You can log in now with your new password.</p>
            <Link to="/login" className="inline-block px-6 py-2.5 rounded-full bg-[#FF7A00] text-black font-bold text-sm">Go to login</Link>
          </div>
        )}
      </div>
    </div>
  );
}
